import type {server} from "@reactivated";

export const DROPPABLE_TYPES = {
    MEAL: "MEAL",
    ENTRY: "ENTRY",
} as const;

export const NUMBER_OF_ENTRIES_BEFORE_ASKING_FOR_RATING = 10;

export const ACCOUNT_AGE_IN_DAYS_BEFORE_ASKING_FOR_RATING = 3;

export const NUMBER_OF_WORKOUTS_BEFORE_ASKING_FOR_RATING = 3;

export const SITE_TINY_WIDTH = 320;

export const SITE_MOBILE_WIDTH = 760;

export const SITE_MENU_HEIGHT = 760;

export const SITE_TABLET_WIDTH = 1024;

export const SITE_MAX_WIDTH = 1200;

export const SITE_MAX_NARROW_WIDTH = 800; // For narrow layouts.

export const VIDEO_MAX_WIDTH = 800;

export const TRIAL_DAYS = 7;

export const KILOJOULES_MULTIPLIER = "0.239006";

export const IOS_APP_STORE_ID = "955172784";

export const ANDROID_APP_STORE_PACKAGE_NAME = "com.joyapp.android";

export const UTM_CONTENT_REGEX = /^v[0-9]-[0-9]+-[\w]+$/;

export const APP_STORE_URL =
    "https://apps.apple.com/app/apple-store/id955172784?pt=120882043&mt=8";

export const PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=com.joyapp.android";

export const INSTAGRAM_URL = "https://www.instagram.com/joyhealthtracker/";

export const FACEBOOK_URL = "https://www.facebook.com/joyhealthtracker/";

export const MAX_TABS_WITH_ICONS = 3;

export type Platform = "web" | "android" | "ios" | "electron";

type NewIcon = keyof typeof server.core.ICON_MAP;

// function icon_usage() { grep -r "\"$1\"" client/ -B 1 -A 1; }

export const ICON_MIGRATION = {
    arrows: "0830-chevrons-expand-vertical", // Drag and drop
    "caret-down": "custom-chevron-down", // For the dropdown on the USDA/All selection on search.
    gear: "0115-cog", // TODO: UNUSED.
    remove: "0130-trash2", // Delete
    home: "0003-home3", // links to the journal on the slide out menu.
    leaf: "0537-platter", // Foods on the menu and the search
    star: "0532-chef", // Recipes on the menu and search
    user: "0284-profile", // Profile
    "bar-chart-o": "0507-graph", // Reports
    send: "0154-paper-plane", // Send invite to user.
    "envelope-o": "custom-0151-envelope", // Contact link on menu.
    "pencil-square-o": "0020-pencil5", // Notes and details tab on update entry/day/recipe.
    calculator: "0360-calculator2", // Calculator section
    bars: "0812-menu", // Menu icon (burger menu)
    spinner: "0721-loading", // Loading indicator
    "info-circle": "0856-question-circle", // Help (switches with indicator)
    bullseye: "0625-archery", // the icon for "Macros", including the primary tab in update food, update day, update recipe, etc.
    list: "0243-equalizer", // The icon for "Advanced", usually next to "macros"
    save: "0175-floppy-disk", // Floppy to save
    magic: "0044-magic-wand", // Benni's favorite.
    plus: "0823-plus", // Add / create / etc. All over the place.
    tachometer: "0310-weight", // Weigh in on journal.
    scissors: "0200-scissors", // Move meals (cut vs copy vs paste being the idea here)
    copy: "0185-copy", // ...
    share: "0801-share2", // ...
    expand: "0845-expand2", // Expand a recipe into its entries on the journal
    compress: "0846-contract2", // Compress a set of entries into a recipe on the journal.
    cutlery: "0531-dinner2", // Icon for the tab showing a recipe's ingredients
    edit: "0020-pencil5", // Make corrections to an entry.
    repeat: "0730-redo2", // For the "add another" action
    eraser: "0018-eraser", // Used to clear the planner. This and remove could probably be similar.
    "credit-card": "0346-credit-card", // On the profile page, the subscription/payment tab.
    "shopping-cart": "0815-list3", // Bulk mode
    "arrow-right": "0837-arrow-right", // "next" on search
    "street-view": "0310-weight", // View the "weight" report on the Reports page.
    heart: "0489-heart-pulse", // View the "fat percentage" report on the Reports page.
    "circle-o-notch": "0892-ruler", // View the "waist size" report the Reports page. Get it? Circle? Waist?
    bolt: "0442-power", // View the energy/calories/kilojoule report on the Reports page.
    "clock-o": "0741-history2", // The recently used tab on the search page
    search: "0803-magnifier", // The database tab on the search page.
    "0859-cross-circle": "0859-cross-circle", // Exit out of manager
} as const;

type heroIcons = ["icons-go-here"];

export type Icon = NewIcon | keyof typeof ICON_MIGRATION | heroIcons[number];

export const CORE_METRICS = [
    ["calories", "calories_pg", "calories_target", "cal", "Calories"],
    ["protein", "protein_pg", "protein_target", "g", "Protein"],
    ["fat", "fat_pg", "fat_target", "g", "Fat"],
    ["carbs", "carbs_pg", "carbs_target", "g", "Carbs"],
    ["fiber", "fiber_pg", "fiber_target", "g", "Fiber"],
    ["net_carbs", "net_carbs_pg", "net_carbs_target", "g", "Net carbs"],
] as const;

export const OPTIONAL_METRICS = [
    ["sodium", "sodium_pg", "sodium_target", "mg", "Sodium"],
    ["saturated_fat", "saturated_fat_pg", "saturated_fat_target", "g", "Saturated fat"],
    [
        "monounsaturated_fat",
        "monounsaturated_fat_pg",
        "monounsaturated_fat_target",
        "g",
        "Monounsaturated fat",
    ],
    [
        "polyunsaturated_fat",
        "polyunsaturated_fat_pg",
        "polyunsaturated_fat_target",
        "g",
        "Polyunsaturated fat",
    ],
    ["trans_fat", "trans_fat_pg", "trans_fat_target", "g", "Trans fat"],
    ["cholesterol", "cholesterol_pg", "cholesterol_target", "mg", "Cholesterol"],
    ["sugars", "sugars_pg", "sugars_target", "g", "Total sugars"],
    ["added_sugars", "added_sugars_pg", "added_sugars_target", "g", "Added sugars"],
    ["potassium", "potassium_pg", "potassium_target", "mg", "Potassium"],
    ["magnesium", "magnesium_pg", "magnesium_target", "mg", "Magnesium"],
    ["vitamin_a_dv", "vitamin_a_dv_pg", "vitamin_a_dv_target", "IU", "Vitamin A"],
    ["vitamin_c_dv", "vitamin_c_dv_pg", "vitamin_c_dv_target", "mg", "Vitamin C"],
    ["calcium_dv", "calcium_dv_pg", "calcium_dv_target", "mg", "Calcium"],
    ["iron_dv", "iron_dv_pg", "iron_dv_target", "mg", "Iron"],
] as const;

export const ALL_METRICS = [...CORE_METRICS, ...OPTIONAL_METRICS];

export const TUTORIALS = [
    ["Adding entries", "search"],
    ["Plan your diet", "planner-2"],
    ["Visualize your progress", "reports"],
    ["Add entries in bulk", "bulk"],
    ["Add a meal", "create-meal"],
    ["View your day summary", "day-summary"],
    ["Copy a meal", "copy-meal"],
    ["Save a meal as a recipe", "recipe-2"],
    ["Create a recipe", "create-recipe-and-add-entry"],
    ["Adjust today's macros", "update-macros"],
    ["Add a quick meal", "add-quick-meal"],
    ["Navigate and make edits", "navigation-and-updates"],
] as const;

export const EXERCISE_TYPES = {
    BENCH: "12fe4e86-47bf-4548-b7e5-5344079ea4eb",
    DEADLIFT: "b39f8b5e-ae3a-4888-b8b1-ba35583e4a74",
    PRESS: "707e96e2-68d0-4408-9189-c34f28ff6185",
    SQUAT: "200e5d12-1488-4259-8cb6-4f3e09f55c63",
} satisfies Record<string, UUID>;

export const ALLOWED_MARKDOWN_ELEMENTS = ["br", "p", "li", "ul", "ol", "strong", "em"];
